import { make } from 'vuex-pathify';
import Logger from '@/lib/logger.js';
import { CaseManagementService } from '@/modules/delinquency/services/case-management.service';

const service = new CaseManagementService();

const checkIfCanForeclosed = (data) => {
  if (!data) return;

  let totalCount = 0;
  let completedCount = 0;
  let foundForeclosure = false;

  for (const e of data) {
    const isForeclosureAction = e.actionName?.indexOf('Foreclosure') > -1;
    if (isForeclosureAction) {
      foundForeclosure = true;
      break;
    }

    if (['completed', 'waived'].includes(e.status) && !isForeclosureAction) {
      completedCount++;
    }

    totalCount++;
  }

  return foundForeclosure && totalCount == completedCount;
};

export const state = {
  //Case States
  loanCases: [], //store cases assigned to specific loan
  fetchingCases: false,
  fetchingAdditionalCaseInfo: false,
  fetchingSelectedCase: false,
  fetchingLoanDetails: false,
  notFoundError: false,
  errorOpeningCase: false,
  cases: [], //store cases assigned to the logged in user
  selectedCase: null,
  otherLoans: [],

  //Case comment states
  totalCaseComments: 0,
  totalCaseCommentsFiles: 0,
  caseComments: [],
  commentsPage: 1,
  commentsLimit: 10,
  fetchingCaseComments: false,

  assignedActions: [],
  fetchingAssignedActions: false,

  selectedLoanDetails: {
    loan: null,
    otherLoans: []
  },

  //Delinquency control action States
  fetchingDelinquencyActions: false,
  updatingDelinquencyActions: false,
  delinquencyActions: [],
  selectedDelinquencyActions: [],
  currentControlAction: '',

  //Collateral States
  canForeclosed: false,
  fetchingCollaterals: false,
  associatedCollaterals: [],

  //External Participants States
  fetchingAssignedExternalParticipants: false,
  assignedExternalParticipants: [],

  //Field Visit States

  fetchingFieldVisits: false,
  fetchingFieldVisit: false,
  selectedFieldVisit: {},
  fieldVisits: [],

  scores: {
    userEfficiencyScore: 0,
    userEfficiencyLevel: '',
    efficiencyScore: 0,
    riskFactorScore: 0,
    efficiencyLevel: ''
  }
};

export const mutations = {
  ...make.mutations(state)
};

export const getters = {
  ...make.getters(state),

  /**
   * Return true if case is opened
   */
  isCaseOpened(state) {
    const selectedCase = state.selectedCase;
    return (
      selectedCase?.status !== 'closed' &&
      ((selectedCase?.type == 'new' && selectedCase?.status == 'opened') ||
        selectedCase?.type == 'reopened' ||
        selectedCase?.status == 'reassigned')
    );
  }
};

export const actions = {
  /**
   * Return true if case is opened
   */
  isCaseOpened({ getters }, caseFile) {
    const selectedCase = caseFile || getters.selectedCase;
    return (
      selectedCase?.status !== 'closed' &&
      ((selectedCase?.type == 'new' && selectedCase?.status == 'opened') ||
        selectedCase?.type == 'reopened' ||
        selectedCase?.status == 'reassigned')
    );
  },

  clearOpenedCase({ commit }) {
    commit('SET_SELECTED_CASE', null);
    commit('SET_COMMENTS_PAGE', 1);
    commit('SET_OTHER_LOANS', []);
    commit('SET_FIELD_VISITS', []);
    commit('SET_DELINQUENCY_ACTIONS', []);
    commit('SET_ASSOCIATED_COLLATERALS', []);
    commit('SET_ASSIGNED_EXTERNAL_PARTICIPANTS', []);
    commit('SET_CASE_COMMENTS', []);

    const url = new URL(window.location);
    url.searchParams.delete('id', url.searchParams.get('id'));
    history.pushState(null, '', url);
  },

  async openCase({ commit, getters, dispatch }, { caseId, readOnly }) {
    try {
      commit('SET_NOT_FOUND_ERROR', false);
      commit('SET_ERROR_OPENING_CASE', false);

      //Loading states
      commit('SET_FETCHING_SELECTED_CASE', true);
      commit('SET_FETCHING_ADDITIONAL_CASE_INFO', true);

      //Data states
      commit('SET_COMMENTS_PAGE', 1);
      commit('SET_OTHER_LOANS', []);
      commit('SET_FIELD_VISITS', []);
      commit('SET_DELINQUENCY_ACTIONS', []);
      commit('SET_ASSOCIATED_COLLATERALS', []);
      commit('SET_ASSIGNED_EXTERNAL_PARTICIPANTS', []);

      if (readOnly) {
        commit('SET_LOAN_CASES', []);
        commit('SET_CASES', []);
      }

      const { data } = await service.getCaseFile(caseId);
      const {
        caseFile,
        otherLoans,
        fieldVisits,
        collaterals,

        externalParticipants,
        delinquencyActionDetails: {
          actions,
          efficiencyScore,
          efficiencyLevel,
          userEfficiencyScore,
          userEfficiencyLevel,
          riskFactorScore
        }
      } = data;

      commit('SET_SELECTED_CASE', caseFile);
      commit('SET_OTHER_LOANS', otherLoans);
      commit('SET_FETCHING_SELECTED_CASE', false);

      await dispatch('getCaseComments', { caseId, page: 1, limit: 10 });

      setTimeout(() => {
        const url = new URL(window.location);
        const hasIdInURL = url.searchParams.has('id');
        if (!readOnly || hasIdInURL) {
          url.searchParams.set('id', caseFile.id);
          history.pushState(null, '', url);
        }

        commit('SET_DELINQUENCY_ACTIONS', actions);
        commit('SET_FIELD_VISITS', fieldVisits);
        commit('SET_FIELD_VISITS', fieldVisits);
        commit('SET_ASSOCIATED_COLLATERALS', collaterals);
        commit('SET_ASSIGNED_EXTERNAL_PARTICIPANTS', externalParticipants);

        commit('SET_CAN_FORECLOSED', checkIfCanForeclosed(actions || []));
        commit('SET_SCORES', {
          userEfficiencyScore: userEfficiencyScore,
          userEfficiencyLevel: userEfficiencyLevel,
          efficiencyScore: efficiencyScore,
          riskFactorScore: riskFactorScore,
          efficiencyLevel: efficiencyLevel
        });
        commit('SET_FETCHING_ADDITIONAL_CASE_INFO', false);
        const cases = getters.cases.map((e) => {
          if (e.id == caseId) {
            e.efficiencyScore = efficiencyScore;
          }
          return e;
        });
        commit('SET_CASES', cases);
      }, 200);

      return caseFile;
    } catch (e) {
      if (e?.status == 404) {
        commit('SET_NOT_FOUND_ERROR', true);
      } else {
        commit('SET_ERROR_OPENING_CASE', true);
      }

      console.error('error: ', e);
    } finally {
      commit('SET_FETCHING_SELECTED_CASE', false);
      commit('SET_FETCHING_DELINQUENCY_ACTIONS', false);
      commit('SET_FETCHING_ADDITIONAL_CASE_INFO', false);
    }
  },

  async findCase({ commit }, { caseId }) {
    try {
      commit('SET_SELECTED_CASE', null);
      const { data } = await service.getCase(caseId);
      commit('SET_SELECTED_CASE', data);
      return data;
    } catch (error) {
      Logger.error(`finding case with id - ${caseId}: `, error);
      throw error;
    }
  },

  async findLoanCases({ commit }, { loanId }) {
    try {
      commit('SET_LOAN_CASES', []);
      commit('SET_FETCHING_CASES', true);
      const { data } = await service.getLoanCases(loanId);
      commit('SET_LOAN_CASES', data);
    } catch (error) {
      Logger.error('finding assigned cases: ', error);
      throw error;
    } finally {
      commit('SET_FETCHING_CASES', false);
    }
  },

  async findCases({ commit }, { userId }) {
    try {
      commit('SET_SELECTED_CASE', null);
      commit('SET_CASES', []);
      commit('SET_FETCHING_CASES', true);
      const { data } = await service.getAssignedCases(userId);
      commit('SET_CASES', data);
    } catch (error) {
      Logger.error('finding assigned cases: ', error);
      throw error;
    } finally {
      commit('SET_FETCHING_CASES', false);
    }
  },

  async addCase({ commit, getters }, { caseDetails }) {
    let cases = getters?.cases || [];
    cases.push(caseDetails);

    commit('SET_CASES', cases);
  },

  async removeCase({ commit, dispatch, getters }, { caseId }) {
    const id = caseId;
    const cases = getters?.cases || [];

    if (cases.length > 0) {
      let updatedCaseList = cases.filter((e) => e.id !== id);

      commit('SET_CASES', updatedCaseList);
      dispatch('clearOpenedCase');
    }
  },

  async closeCase({ dispatch, getters }, { caseId, closureReason, comment }) {
    try {
      const id = caseId ?? getters?.selectedCase?.id;
      const { data } = await service.closeCase(id, { closureReason, comment });

      dispatch('clearOpenedCase');
      return data;
    } catch (error) {
      Logger.error('finding assigned cases: ', error);
      throw error;
    }
  },

  async findDelinquencyActions({ commit, getters }, { caseId }) {
    try {
      commit('SET_DELINQUENCY_ACTIONS', []);
      commit('SET_FETCHING_DELINQUENCY_ACTIONS', true);

      const { data } = await service.getDelinquencyActions(caseId);

      commit('SET_CAN_FORECLOSED', checkIfCanForeclosed(data?.actions || []));
      commit('SET_DELINQUENCY_ACTIONS', data.actions);
      commit('SET_SCORES', {
        efficiencyScore: data.efficiencyScore,
        riskFactorScore: data.riskFactorScore,
        efficiencyLevel: data.efficiencyLevel
      });

      const cases = getters.cases.map((e) => {
        if (e.id == caseId) {
          e.efficiencyScore = data.efficiencyScore;
        }
        return e;
      });
      commit('SET_CASES', cases);
    } catch (error) {
      Logger.error(`finding case with id - ${caseId}: `, error);
      throw error;
    } finally {
      commit('SET_FETCHING_DELINQUENCY_ACTIONS', false);
    }
  },

  async saveDelinquencyActionTemplate(
    { commit, getters },
    { delinquencyActionId, templateDetails }
  ) {
    try {
      const currentCase = getters.selectedCase;
      await service.saveDelinquencyActionTemplate(
        currentCase.id,
        delinquencyActionId,
        templateDetails
      );

      const actions = getters.delinquencyActions || [];
      const modifiedActions = actions.map((el) =>
        delinquencyActionId == el.id
          ? { ...el, templateDetails: templateDetails }
          : el
      );

      commit('SET_DELINQUENCY_ACTIONS', modifiedActions);
    } catch (error) {
      Logger.error(`saving delinquency action template: `, error);
      throw error;
    }
  },

  async assignDelinquencyAction(
    { commit, getters },
    { delinquencyActionId, userId }
  ) {
    try {
      const currentCase = getters.selectedCase;
      const { data } = await service.assignDelinquencyAction(
        currentCase.id,
        delinquencyActionId,
        userId
      );

      const actions = getters.delinquencyActions || [];
      const modifiedActions = actions.map((el) =>
        data.id == el.id ? data : el
      );
      commit('SET_DELINQUENCY_ACTIONS', modifiedActions);
    } catch (error) {
      Logger.error(`assigning deliqency action to user: `, error);
      throw error;
    }
  },

  async unassignDelinquencyAction(
    { commit, getters },
    { delinquencyActionId, userId }
  ) {
    try {
      const currentCase = getters.selectedCase;

      await service.unassignDelinquencyAction(
        currentCase.id,
        delinquencyActionId,
        userId
      );
      const actions = getters.delinquencyActions || [];
      const modifiedActions = actions.map((el) => {
        if (delinquencyActionId == el.id) {
          el.delinquencyActionAssignment = null;
        }
        return el;
      });
      commit('SET_DELINQUENCY_ACTIONS', modifiedActions);
    } catch (error) {
      Logger.error(`unassigning user from deliqency action`, error);
      throw error;
    }
  },

  async updateDelinquencyActionStatus(
    { commit, getters, dispatch },
    { delinquencyActionId, statusDetails, fileList }
  ) {
    try {
      const currentCase = getters.selectedCase;

      const { data } = await service.updateDelinquencyActionStatus(
        currentCase.id,
        delinquencyActionId,
        statusDetails,
        fileList
      );

      commit('SET_CAN_FORECLOSED', checkIfCanForeclosed(data?.actions || []));
      commit('SET_DELINQUENCY_ACTIONS', data.actions);

      commit('SET_SCORES', {
        efficiencyScore: data.efficiencyScore,
        riskFactorScore: data.riskFactorScore,
        efficiencyLevel: data.efficiencyLevel,
        userEfficiencyLevel: data.userEfficiencyLevel ?? '-',
        userEfficiencyScore: data.userEfficiencyScore ?? ''
      });

      const cases = getters.cases.map((e) => {
        if (e.id == currentCase.id) {
          e.efficiencyScore = data.efficiencyScore;
        }
        return e;
      });

      commit('SET_CASES', cases);

      if (statusDetails.comment) {
        await dispatch('getCaseComments', {
          caseId: currentCase.id,
          page: 1,
          limit: 10
        });
      }
    } catch (error) {
      Logger.error(`updating deliqency action status`, error);
      throw error;
    } finally {
      commit('SET_FETCHING_DELINQUENCY_ACTIONS', false);
    }
  },

  async escalateCase({ getters, commit, dispatch }, { escalateeId, comment }) {
    try {
      const currentCase = getters.selectedCase;

      await service.escalateCase(currentCase.id, {
        comment,
        escalateeId
      });

      let cases = getters.cases;
      const updatedCase = await dispatch('findCase', {
        caseId: currentCase.id
      });
      cases = cases.map((e) => (e.id == updatedCase.id ? updatedCase : e));

      commit('SET_CASES', cases);
    } catch (error) {
      Logger.error(`escalating case`, error);
      throw error;
    }
  },

  async updatePersonInformation({ commit, getters }, updatedDetails) {
    //Update person's information in the currently selected case and the overall cases

    let cases = getters.cases;
    const currentCase = getters.selectedCase;

    if (currentCase?.delinquencyCase?.loan?.loanDistributions) {
      currentCase.delinquencyCase.loan.loanDistributions =
        currentCase?.delinquencyCase?.loan?.loanDistributions?.map((e) => {
          if (e.person.id === updatedDetails.id) {
            e.person = updatedDetails;
            return e;
          }
          return e;
        });
    }

    cases = cases.map((e) => (e.id == currentCase.id ? currentCase : e));
    commit('SET_CASES', cases);
    commit('SET_SELECTED_CASE', currentCase);
  },

  async findOtherLoans({ commit }, { caseId }) {
    try {
      commit('SET_OTHER_LOANS', []);
      const { data } = await service.getOtherLoans(caseId);
      commit('SET_OTHER_LOANS', data);
    } catch (error) {
      Logger.error(`finding case with id - ${caseId}: `, error);
      throw error;
    }
  },

  async findAssociatedCollaterals({ commit }, { caseId }) {
    try {
      commit('SET_FETCHING_COLLATERALS', true);
      commit('SET_ASSOCIATED_COLLATERALS', []);
      const { data } = await service.getAssociatedCollaterals(caseId);
      commit('SET_ASSOCIATED_COLLATERALS', data);
    } catch (error) {
      Logger.error(
        `finding collaterals associated with case id - ${caseId}: `,
        error
      );
      throw error;
    } finally {
      commit('SET_FETCHING_COLLATERALS', false);
    }
  },

  async findLoanDetails({ commit }, { loanId }) {
    try {
      commit('SET_FETCHING_LOAN_DETAILS', true);
      commit('SET_SELECTED_LOAN_DETAILS', {
        loan: null,
        otherLoans: []
      });

      const { data } = await service.getLoanDetails(loanId);
      commit('SET_SELECTED_LOAN_DETAILS', {
        loan: data.loan,
        otherLoans: data.otherLoans
      });
    } catch (error) {
      Logger.error(`finding loan with id - ${loanId}: `, error);
      commit('SET_FETCHING_LOAN_DETAILS', false);
      throw error;
    }
  },

  async findExternalParticipants({ commit }, { caseId }) {
    try {
      commit('SET_FETCHING_ASSIGNED_EXTERNAL_PARTICIPANTS', true);
      commit('SET_ASSIGNED_EXTERNAL_PARTICIPANTS', []);

      const { data } = await service.getExternalParticipants(caseId);
      commit('SET_ASSIGNED_EXTERNAL_PARTICIPANTS', data);
    } catch (error) {
      Logger.error(`finding external participants: `, error);
      commit('SET_FETCHING_ASSIGNED_EXTERNAL_PARTICIPANTS', false);
      throw error;
    } finally {
      commit('SET_FETCHING_ASSIGNED_EXTERNAL_PARTICIPANTS', false);
    }
  },

  async assignExternalParticipant(
    { commit, getters },
    { externalParticipantId, dateAssigned }
  ) {
    try {
      const { data } = await service.assignExternalParticipant(
        getters.selectedCase?.id,
        externalParticipantId,
        { dateAssigned }
      );

      commit('SET_ASSIGNED_EXTERNAL_PARTICIPANTS', [
        ...getters.assignedExternalParticipants,
        data
      ]);
    } catch (error) {
      Logger.error(`assigning external participants: `, error);
      throw error;
    }
  },

  async unassignExternalParticipant(
    { commit, getters },
    { externalParticipantId }
  ) {
    const assignedExternalParticipants = getters.assignedExternalParticipants;

    try {
      await service.unassignExternalParticipant(
        getters.selectedCase?.id,
        externalParticipantId
      );

      commit('SET_ASSIGNED_EXTERNAL_PARTICIPANTS', [
        ...assignedExternalParticipants.filter(
          (e) => e.externalParticipant?.id !== externalParticipantId
        )
      ]);
    } catch (error) {
      Logger.error(`assigning external participants: `, error);
      throw error;
    }
  },

  async saveExternalParticipantInformation(
    { commit, getters },
    { externalParticipantId, information }
  ) {
    const list = getters.assignedExternalParticipants.filter(
      (e) => e.externalParticipant?.id == externalParticipantId
    );

    const assignedExternalParticipant = list[0];
    if (!assignedExternalParticipant) return false;

    try {
      const caseId = getters.selectedCase?.id;

      switch (
        assignedExternalParticipant?.externalParticipant?.type.toLowerCase()
      ) {
        case 'collector':
          await service.saveCollectorInformation(
            caseId,
            externalParticipantId,
            information
          );
          break;
        case 'bailiff':
          await service.saveBailiffInformation(
            caseId,
            externalParticipantId,
            information
          );
          break;

        case 'legal representative':
          await service.saveLegalRepresentativeInformation(
            caseId,
            externalParticipantId,
            information
          );
          break;
        default:
          return false;
      }

      //Update information locally
      assignedExternalParticipant.information = { ...information };
      commit('SET_ASSIGNED_EXTERNAL_PARTICIPANTS', [
        ...getters.assignedExternalParticipants.map((e) => {
          if (e.externalParticipant?.id === externalParticipantId) {
            return assignedExternalParticipant;
          }
          return e;
        })
      ]);

      return true;
    } catch (error) {
      Logger.error(`save external participants information: `, error);
      throw error;
    }
  },

  async findFieldVisit({ commit, getters }, { id }) {
    try {
      const selectedCase = getters.selectedCase;

      commit('SET_FETCHING_FIELD_VISIT', true);
      commit('SET_SELECTED_FIELD_VISIT', {});

      const { data } = await service.getFieldVisit(selectedCase.id, id);

      commit('SET_SELECTED_FIELD_VISIT', data);
    } catch (error) {
      Logger.error('finding field visits: ', error);
      throw error;
    } finally {
      commit('SET_FETCHING_FIELD_VISIT', false);
    }
  },

  async findFieldVisits({ commit }, { caseId }) {
    try {
      commit('SET_FETCHING_FIELD_VISITS', true);
      commit('SET_FIELD_VISITS', []);
      const { data } = await service.getFieldVisits(caseId);
      commit('SET_FIELD_VISITS', data);
    } catch (error) {
      Logger.error('finding field visits: ', error);
      throw error;
    } finally {
      commit('SET_FETCHING_FIELD_VISITS', false);
    }
  },

  async saveFieldVisit({ commit, getters }, { fieldVisit, fileList }) {
    try {
      let fieldVisits = getters.fieldVisits;
      const selectedCase = getters.selectedCase;

      commit('SET_FIELD_VISITS', []);

      if (!fieldVisit.id) {
        delete fieldVisit.id;
      }

      const { data } = await service.saveFieldVisit(
        selectedCase.id,
        fieldVisit,
        fileList
      );
      const savedFieldVisit = data || fieldVisit;

      if (fieldVisit.id) {
        fieldVisits = fieldVisits.map((e) =>
          e.id == fieldVisit.id ? savedFieldVisit : e
        );
      } else {
        fieldVisits.push(savedFieldVisit);
      }

      commit('SET_FIELD_VISITS', fieldVisits);
    } catch (error) {
      Logger.error('saving field visits: ', error);
      throw error;
    }
  },

  async removeFieldVisit({ commit, getters }, { fieldVisit }) {
    try {
      let fieldVisits = getters.fieldVisits;
      const selectedCase = getters.selectedCase;

      await service.deleteFieldVisit(selectedCase.id, fieldVisit.id);
      fieldVisits = fieldVisits.filter((e) => e.id !== fieldVisit.id);

      commit('SET_FIELD_VISITS', fieldVisits);
    } catch (error) {
      Logger.error('saving field visits: ', error);
      throw error;
    }
  },
  async findAssignedActions({ commit }) {
    try {
      commit('SET_ASSIGNED_ACTIONS', []);
      commit('SET_FETCHING_ASSIGNED_ACTIONS', true);
      const { data } = await service.getAssignedActions();
      commit('SET_ASSIGNED_ACTIONS', data);
    } catch (error) {
      Logger.error('finding assigned actions: ', error);
      commit('SET_ASSIGNED_ACTIONS', []);
      throw error;
    } finally {
      commit('SET_FETCHING_ASSIGNED_ACTIONS', false);
    }
  },
  async createForeclosureCase({ getters }, { foreclosureCase }) {
    try {
      await service.createForeclosureCase(
        getters.selectedCase?.id,
        foreclosureCase
      );
    } catch (error) {
      Logger.error(`creating foreclosure case: `, error);
      throw error;
    }
  },

  async updateLoanDetails(
    { getters, dispatch },
    { pastDueDays, currentBalance, interestArrears, principalArrears }
  ) {
    try {
      const caseId = getters.selectedCase?.id;
      await service.updateLoanDetails(caseId, {
        pastDueDays,
        currentBalance,
        interestArrears,
        principalArrears
      });

      await dispatch('findCase', { caseId });
    } catch (error) {
      Logger.error(`updating loan details: `, error);
      throw error;
    }
  },

  async getCaseComments(
    { getters, commit },
    { search = '', caseId, page, limit }
  ) {
    try {
      commit('SET_CASE_COMMENTS', []);
      commit('SET_TOTAL_CASE_COMMENTS', 0);
      commit('SET_TOTAL_CASE_COMMENTS_FILES', 0);
      commit('SET_FETCHING_CASE_COMMENTS', true);

      const id = caseId ?? getters.selectedCase?.id;
      const { data } = await service.getComments(id, {
        search: search ?? '',
        page,
        limit
      });

      commit('SET_TOTAL_CASE_COMMENTS', data.total ?? 0);
      commit('SET_TOTAL_CASE_COMMENTS_FILES', data.totalFiles ?? 0);
      commit('SET_CASE_COMMENTS', data.data ?? []);
    } catch (error) {
      Logger.error(`getting comments for case: `, error);
      throw error;
    } finally {
      commit('SET_FETCHING_CASE_COMMENTS', false);
    }
  },

  async loadMoreCommentsForCase(
    { getters, commit },
    { search = '', caseId, page, limit }
  ) {
    const id = caseId ?? getters.selectedCase?.id;
    const comments = getters.caseComments ?? [];

    commit('SET_FETCHING_CASE_COMMENTS', true);
    if (!page) {
      commit('SET_COMMENTS_PAGE', getters.commentsPage + 1);
    }

    const currentPage = page || getters?.commentsPage;
    const currentLimit = limit || getters?.commentsLimit;

    try {
      const { data } = await service.getComments(id, {
        search: search ?? '',
        page: currentPage,
        limit: currentLimit
      });

      commit('SET_TOTAL_CASE_COMMENTS', data.total);
      commit('SET_CASE_COMMENTS', [...comments, ...(data.data ?? [])]);
    } catch (error) {
      Logger.error(`getting more comments for case: `, error);
      throw error;
    } finally {
      commit('SET_FETCHING_CASE_COMMENTS', false);
    }
  },

  async createCommentOnCase(
    { getters, dispatch },
    { userId, type, message, fileList }
  ) {
    try {
      const caseId = getters.selectedCase?.id;
      await service.createComment(
        caseId,
        {
          userId,
          type,
          message
        },
        fileList
      );

      dispatch('getCaseComments', { caseId, page: 1, limit: 10 });
    } catch (error) {
      Logger.error(`creating comment on case: `, error);
      throw error;
    }
  },

  async updateCommentOnCase(
    { getters, commit },
    { id, message, evidenceToDeleteUrls, fileList }
  ) {
    try {
      const caseId = getters.selectedCase?.id;
      const { data } = await service.updateComment(
        caseId,
        id,
        {
          message,
          evidenceToDeleteUrls: evidenceToDeleteUrls ?? []
        },
        fileList
      );

      const comments = getters.caseComments?.map((el) => {
        if (el.id == id) {
          return data;
        }
        return el;
      });

      commit('SET_CASE_COMMENTS', comments);
    } catch (error) {
      Logger.error(`creating comment on case: `, error);
      throw error;
    }
  },

  async removeCommentFromCase({ getters, dispatch }, { id }) {
    try {
      const caseId = getters.selectedCase?.id;
      await service.deleteComment(caseId, id);

      await dispatch('getCaseComments', { caseId, page: 1, limit: 10 });
    } catch (error) {
      Logger.error(`removing comment on case: `, error);
      throw error;
    }
  }
};
